import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { salsaAnalytics } from '../services/salsaAnalytics'

const ONZOE_WIDGET_KEY = 'onzoe-premier-living-signal'

interface OnzoeWidgetEventDetail {
  event_type?: string
  tour_id?: string
  session_id?: string
  attributes?: Record<string, unknown>
}

declare global {
  interface Window {
    OnzoeWidget?: {
      init: (config: Record<string, unknown>) => Promise<void>
      startTour: (tourId: string) => boolean
      refreshTours?: () => number
      open?: () => void
    }
  }
}

export function OnzoePremierWidget() {
  const location = useLocation()

  useEffect(() => {
    if (import.meta.env.DEV && !['/', '/demo/tour'].includes(location.pathname)) return undefined
    let cancelled = false

    const initialize = async () => {
      if (cancelled || !window.OnzoeWidget) return
      await window.OnzoeWidget.init({
        widgetKey: ONZOE_WIDGET_KEY,
        tourOnly: false,
        analytics: { enabled: true },
        brandName: 'Onzoe',
        agentName: 'Onzoe',
      })
    }

    const existing = document.querySelector<HTMLScriptElement>('script[data-onzoe-premier-widget="true"]')
    if (existing) {
      if (window.OnzoeWidget) void initialize()
      else existing.addEventListener('load', () => void initialize(), { once: true })
    } else {
      const script = document.createElement('script')
      script.src = '/widget.js'
      script.async = true
      script.dataset.onzoePremierWidget = 'true'
      script.addEventListener('load', () => void initialize(), { once: true })
      document.head.appendChild(script)
    }

    const trackTourEvent = (event: Event) => {
      const detail = (event as CustomEvent<OnzoeWidgetEventDetail>).detail
      if (!detail?.event_type) return
      salsaAnalytics.track(`public_${detail.event_type}`, {
        tour_id: detail.tour_id,
        tour_session_id: detail.session_id,
        ...(detail.attributes || {}),
      })
    }
    window.addEventListener('onzoe:tour-event', trackTourEvent)

    return () => {
      cancelled = true
      window.removeEventListener('onzoe:tour-event', trackTourEvent)
    }
  }, [location.pathname])

  return null
}
